import React, { useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import type { StatCardModel } from '@/types/stats';
import { PageHeader } from '@/components/composites/PageHeader';
import { StatCardGrid } from '@/components/composites/StatCardGrid';
import { Button } from '@/components/primitives/Button';
import { Select } from '@/components/primitives/Select';
import { TextInput } from '@/components/primitives/TextInput';
import { useAppContext } from '@/context/AppContext';
import { currentDate } from '@/lib/dates';
import {
  formatIsoWeek,
  formatYearMonth,
  isoWeekOf,
  parsePeriodFromSearch,
  periodLabel,
  periodToDateRangeParams,
  periodToQueryString,
  periodToSearchParams,
  quarterOf,
  thisMonthDateRange,
  type PeriodKind,
  type PeriodSelection,
} from '@/lib/period';
import type { MonthlyPersonRow, MonthlyClientGroup } from '@/data/monthlyReport';
import { useMonthlyReport } from '@/hooks/useMonthlyReport';
import { noop } from '@/lib/noop';
import styles from './ReportsMonthlyPage.module.css';

const CLIENT_OPTIONS = [{ value: 'all', label: 'All Clients' }];

const PERIOD_OPTIONS: ReadonlyArray<{ value: PeriodKind; label: string }> = [
  { value: 'month', label: 'Month' },
  { value: 'week', label: 'Week' },
  { value: 'quarter', label: 'Quarter' },
  { value: 'custom', label: 'Custom' },
];

function Dash() {
  return <span className={styles.dash}>&mdash;</span>;
}

interface PersonAccordionProps {
  person: MonthlyPersonRow;
  isOpen: boolean;
  onToggle: () => void;
  /** Task 156 D3 — `?startDate=…&endDate=…` carried through every drill-down link. */
  rangeQs: string;
}

function PersonAccordion({ person, isOpen, onToggle, rangeQs }: PersonAccordionProps) {
  const hasClients = person.clients && person.clients.length > 0;

  return (
    <tbody className={styles.accordionGroup}>
      {/* Person summary row */}
      <tr
        className={`${styles.rowPerson} ${!isOpen ? styles.rowCollapsed : ''}`}
        onClick={onToggle}
        style={{ cursor: 'pointer' }}
      >
        <td>
          <span className={`${styles.chevron} ${isOpen ? styles.chevronOpen : ''}`}>&#9654;</span>
          {/* Task 156 D3 — employee name links to the Employee Detail page
              with the active date range carried over. */}
          {person.id ? (
            <Link
              to={`/employees/${person.id}?${rangeQs}`}
              className={styles.linkCell}
              onClick={(e) => e.stopPropagation()}
            >
              {person.name}
            </Link>
          ) : (
            person.name
          )}
        </td>
        <td className={styles.mono}>{person.hours}</td>
        <td className={styles.mono}>{person.billable}</td>
        <td className={styles.mono}>{person.general}</td>
        <td className={styles.mono}>{person.events}</td>
        <td className={styles.right}>
          {!isOpen && person.collapsedHint ? (
            <span className={styles.expandHint}>{person.collapsedHint}</span>
          ) : (
            <div className={styles.barCellWrap}>
              <div className={styles.barCell}>
                <div className={styles.barFill} style={{ width: `${person.pct}%` }} />
              </div>
              <span className={styles.barPct}>{person.pct}%</span>
            </div>
          )}
        </td>
      </tr>

      {/* Expanded client/project children */}
      {isOpen &&
        hasClients &&
        (person.clients ?? []).map((client: MonthlyClientGroup, ci: number) =>
          client.name ? (
            <React.Fragment key={ci}>
              <tr className={styles.rowClient}>
                <td style={{ paddingLeft: 'var(--space-10)' }}>
                  {/* Task 156 D3 — client row links to the Client Detail
                      page when an id is known; otherwise plain text. */}
                  {client.id ? (
                    <Link to={`/clients/${client.id}?${rangeQs}`} className={styles.linkCell}>
                      {client.name}
                    </Link>
                  ) : (
                    client.name
                  )}
                </td>
                <td></td>
                <td></td>
                <td></td>
                <td></td>
                <td></td>
              </tr>
              {client.projects.map((proj, pi) => (
                <tr key={pi} className={styles.rowProject}>
                  <td style={{ paddingLeft: 'var(--space-16)' }}>
                    {/* Task 156 D3 — project row links to ProjectDetailPage
                        with the active range; renders as plain text when no
                        id is available (mockup mode). The current
                        ProjectDetailPage shows the full project list, so
                        the project id is not yet a path segment — pass it
                        as a query param for forward-compat. */}
                    {proj.id ? (
                      <Link
                        to={{
                          pathname: '/projects/detail',
                          search: `?projectId=${encodeURIComponent(proj.id)}&${rangeQs}`,
                        }}
                        className={styles.linkCell}
                      >
                        {proj.name}
                      </Link>
                    ) : (
                      proj.name
                    )}
                  </td>
                  <td className={styles.mono}>{proj.hours}</td>
                  <td className={styles.mono}>{proj.billable ?? <Dash />}</td>
                  <td className={styles.mono}>{proj.general ?? <Dash />}</td>
                  <td className={styles.mono}>{proj.events}</td>
                  <td></td>
                </tr>
              ))}
            </React.Fragment>
          ) : (
            <React.Fragment key={ci}>
              {/* Task 162 D2 — Rule 23 fix: the "general bucket" row (a
                  client with name === null) shows an em dash in the client
                  cell rather than the literal "(General)". Matches the
                  ManageProjectsPage convention; uses the local <Dash>
                  component for in-file consistency. */}
              <tr className={styles.rowGeneral}>
                <td style={{ paddingLeft: 'var(--space-10)' }}>
                  <Dash />
                </td>
                <td></td>
                <td></td>
                <td></td>
                <td></td>
                <td></td>
              </tr>
              {client.projects.map((proj, pi) => (
                <tr key={pi} className={styles.rowGeneralProject}>
                  <td style={{ paddingLeft: 'var(--space-16)' }}>
                    {proj.id ? (
                      <Link
                        to={{
                          pathname: '/projects/detail',
                          search: `?projectId=${encodeURIComponent(proj.id)}&${rangeQs}`,
                        }}
                        className={styles.linkCell}
                      >
                        {proj.name}
                      </Link>
                    ) : (
                      proj.name
                    )}
                  </td>
                  <td className={styles.mono}>{proj.hours}</td>
                  <td className={styles.mono}>{proj.billable ?? <Dash />}</td>
                  <td className={styles.mono}>{proj.general ?? <Dash />}</td>
                  <td className={styles.mono}>{proj.events}</td>
                  <td></td>
                </tr>
              ))}
            </React.Fragment>
          ),
        )}
    </tbody>
  );
}

export function ReportsMonthlyPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { mockupMode } = useAppContext();

  // Task 156 D2 — `now` is captured once per render so the default-period
  // computation stays stable inside the same render cycle. The
  // currentDate() helper is the project-wide "wall-clock instant" hook.
  const now = useMemo(() => currentDate(), []);

  const period: PeriodSelection = useMemo(
    () => parsePeriodFromSearch(searchParams, now),
    [searchParams, now],
  );

  const queryString = periodToQueryString(period);
  // Task 166 — only the `people` rows are read here; the `stats` array is
  // intentionally unused now that the KPI strip and the footer both derive
  // from the rendered rows. We leave the hook return shape untouched
  // (out-of-scope: refactor `useMonthlyReport`).
  const { people: monthlyPeople } = useMonthlyReport(mockupMode ? null : queryString);

  const [expanded, setExpanded] = useState<Record<string, boolean>>({
    'sarah-chen': true,
    'marcus-rivera': true,
  });

  const toggle = (id: string) => setExpanded((prev) => ({ ...prev, [id]: !prev[id] }));

  // Task 166 — fix the KPI/footer discrepancy by deriving both the KPI
  // strip and the table footer from the same rendered-row data
  // (`monthlyPeople`). Previously the footer cells were hardcoded literals
  // (`847.5h` / `612.0h` / `235.5h` / `10,171`) that matched the mockup
  // fixture by coincidence and disagreed with any other state — including
  // every live-mode response. Precedent: task 154 did the same for the
  // Employee Detail page's "Total" footer.
  const totals = useMemo(() => {
    let hours = 0;
    let billable = 0;
    let general = 0;
    let events = 0;
    for (const p of monthlyPeople) {
      hours += parseFloat(p.hours);
      billable += parseFloat(p.billable);
      general += parseFloat(p.general);
      events += parseInt(p.events.replace(/,/g, ''), 10);
    }
    return { hours, billable, general, events };
  }, [monthlyPeople]);

  const cards: readonly [StatCardModel, StatCardModel, StatCardModel, StatCardModel] = [
    { label: 'Total Hours', value: `${totals.hours.toFixed(1)}h` },
    { label: 'Billable Hours', value: `${totals.billable.toFixed(1)}h` },
    { label: 'General Hours', value: `${totals.general.toFixed(1)}h` },
    { label: 'Team Members', value: String(monthlyPeople.length) },
  ];

  // ── Period-selector handlers ──────────────────────────────────────────
  // When the user picks a new period kind, populate the kind-specific
  // params with sensible defaults (this month / this week / this quarter)
  // so the page renders something the moment the kind changes.
  const onChangeKind = (kind: PeriodKind) => {
    let next: PeriodSelection;
    switch (kind) {
      case 'month':
        next = { kind: 'month', month: formatYearMonth(now) };
        break;
      case 'week': {
        const { year, week } = isoWeekOf(now);
        next = { kind: 'week', week: formatIsoWeek(year, week) };
        break;
      }
      case 'quarter': {
        const { year, quarter } = quarterOf(now);
        next = { kind: 'quarter', quarter: `${year}-Q${quarter}` };
        break;
      }
      case 'custom': {
        const r = thisMonthDateRange(now);
        next = { kind: 'custom', startDate: r.startDate, endDate: r.endDate };
        break;
      }
    }
    setSearchParams(periodToSearchParams(next));
  };

  const onChangePeriodValue = (val: string) => {
    let next: PeriodSelection;
    if (period.kind === 'month') next = { kind: 'month', month: val };
    else if (period.kind === 'week') next = { kind: 'week', week: val };
    else if (period.kind === 'quarter') next = { kind: 'quarter', quarter: val };
    else return; // custom uses two inputs handled separately
    setSearchParams(periodToSearchParams(next));
  };

  const onChangeCustomStart = (val: string) => {
    setSearchParams(
      periodToSearchParams({
        kind: 'custom',
        startDate: val,
        endDate: period.endDate ?? val,
      }),
    );
  };

  const onChangeCustomEnd = (val: string) => {
    setSearchParams(
      periodToSearchParams({
        kind: 'custom',
        startDate: period.startDate ?? val,
        endDate: val,
      }),
    );
  };

  // ── Drill-down range params ───────────────────────────────────────────
  // Every link in the table needs a `?startDate=…&endDate=…` suffix so the
  // detail pages render the same window the user is staring at.
  const range = periodToDateRangeParams(period, now);
  const rangeQs = `startDate=${encodeURIComponent(range.startDate)}&endDate=${encodeURIComponent(range.endDate)}`;

  return (
    <>
      <PageHeader title="Monthly Report" />

      {/* Controls bar — task 156 D2 swap of the Feb/Mar/Apr month buttons
          for a Period selector. The kind dropdown drives which secondary
          input renders (a YYYY-MM input for month, YYYY-W## for week,
          YYYY-Q# for quarter, or two YYYY-MM-DD inputs for custom). */}
      <div className={styles.reportControls}>
        <div className={styles.periodNav} data-testid="period-selector">
          <Select
            value={period.kind}
            options={PERIOD_OPTIONS}
            onChange={(v) => onChangeKind(v as PeriodKind)}
            size="sm"
            label="Period"
          />
          {/* Task 162 D1 — Rule 19 fix: raw <input>s replaced with the
              <TextInput> primitive. The primitive spreads InputHTMLAttributes
              so type/pattern/aria-label/data-testid pass through to the
              underlying <input>; the existing test-ids (asserted in
              ReportsMonthlyPage.test.tsx) survive the swap. */}
          {period.kind === 'month' && (
            <TextInput
              type="month"
              size="sm"
              data-testid="period-month-input"
              value={period.month ?? ''}
              onChange={(e) => onChangePeriodValue(e.target.value)}
              aria-label="Month"
            />
          )}
          {period.kind === 'week' && (
            <TextInput
              type="week"
              size="sm"
              data-testid="period-week-input"
              value={period.week ?? ''}
              onChange={(e) => onChangePeriodValue(e.target.value)}
              aria-label="Week"
            />
          )}
          {period.kind === 'quarter' && (
            <TextInput
              type="text"
              size="sm"
              data-testid="period-quarter-input"
              value={period.quarter ?? ''}
              onChange={(e) => onChangePeriodValue(e.target.value)}
              aria-label="Quarter"
              placeholder="2026-Q1"
              pattern="\d{4}-Q[1-4]"
            />
          )}
          {period.kind === 'custom' && (
            <>
              <TextInput
                type="date"
                size="sm"
                data-testid="period-startdate-input"
                value={period.startDate ?? ''}
                onChange={(e) => onChangeCustomStart(e.target.value)}
                aria-label="Start date"
              />
              <span className={styles.periodSeparator}>to</span>
              <TextInput
                type="date"
                size="sm"
                data-testid="period-enddate-input"
                value={period.endDate ?? ''}
                onChange={(e) => onChangeCustomEnd(e.target.value)}
                aria-label="End date"
              />
            </>
          )}
          <span className={styles.monthCurrent} data-testid="period-label">
            {periodLabel(period)}
          </span>
        </div>
        <div className={styles.reportActions}>
          <Select value="all" options={CLIENT_OPTIONS} onChange={noop} size="sm" />
          <Button variant="secondary" onClick={noop} size="sm">
            Export CSV
          </Button>
          <Button variant="secondary" onClick={noop} size="sm">
            Print
          </Button>
        </div>
      </div>

      <StatCardGrid cards={cards} />

      {/* Rule 9 exception: hierarchical person→client→project grouping requires
          custom row rendering that DataTable's flat-row model cannot express. */}
      <div className={styles.tableWrap} data-testid="data-table">
        <table className={styles.table}>
          <caption className={styles.srOnly}>Monthly report by person, client, and project</caption>
          <thead>
            <tr>
              <th>Name / Category</th>
              <th className={styles.right}>Hours</th>
              <th className={styles.right}>Billable</th>
              <th className={styles.right}>General</th>
              <th className={styles.right}>Events</th>
              <th className={styles.right} style={{ width: 120 }}>
                % of Total
              </th>
            </tr>
          </thead>

          {monthlyPeople.map((person) => (
            <PersonAccordion
              key={person.id}
              person={person}
              isOpen={!!expanded[person.id]}
              onToggle={() => toggle(person.id)}
              rangeQs={rangeQs}
            />
          ))}

          <tfoot>
            {/* Task 166 — footer values are computed from `monthlyPeople`
                in `totals` above so the row always agrees with the KPI
                strip. The hardcoded literals (`847.5h`, `612.0h`,
                `235.5h`, `10,171`) that matched the mockup fixture by
                coincidence have been removed. */}
            <tr data-testid="monthly-grand-total-row">
              <td>Grand Total</td>
              <td className={styles.mono} data-testid="monthly-grand-total-hours">
                {`${totals.hours.toFixed(1)}h`}
              </td>
              <td className={styles.mono}>{`${totals.billable.toFixed(1)}h`}</td>
              <td className={styles.mono}>{`${totals.general.toFixed(1)}h`}</td>
              <td className={styles.mono}>{totals.events.toLocaleString()}</td>
              <td className={styles.right}>
                <div className={styles.barCellWrap}>
                  <div className={styles.barCell}>
                    <div className={styles.barFill} style={{ width: '100%' }} />
                  </div>
                  <span className={styles.barPct}>100%</span>
                </div>
              </td>
            </tr>
          </tfoot>
        </table>
      </div>
    </>
  );
}
